import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { normalizeStarRating } from '@/features/customers/reviews/utils/ratings'

/**
 * Known GMB location mappings for FixMyBrick business locations
 * Based on confirmed location IDs with substantial review counts
 */
const KNOWN_GMB_LOCATIONS: Record<string, { name: string; city: string; region: string }> = {
  // Hamilton - confirmed
  '13164342179722993234': {
    name: 'FixMyBrick Hamilton',
    city: 'Hamilton',
    region: 'hamilton'
  },
  // Mississauga - confirmed
  '14646597005547091058': {
    name: 'FixMyBrick Mississauga',
    city: 'Mississauga',
    region: 'mississauga'
  },
  // Kitchen-Waterloo-Guelph - confirmed
  '1145415718509784391': {
    name: 'FixMyBrick Kitchen-Waterloo-Guelph',
    city: 'Kitchen-Waterloo-Guelph',
    region: 'kitchen-waterloo-guelph'
  },
  // Toronto - confirmed
  '11383209896739779307': {
    name: 'FixMyBrick Toronto',
    city: 'Toronto',
    region: 'toronto'
  },
  // London - confirmed
  '16896183393960554512': {
    name: 'FixMyBrick London',
    city: 'London',
    region: 'london'
  },
  // Niagara - confirmed
  '4921508762966267341': {
    name: 'FixMyBrick Niagara',
    city: 'Niagara',
    region: 'niagara'
  }
}

/**
 * Get location display name for a GMB location ID
 */
function getLocationDisplayName(locationId: string): string {
  const known = KNOWN_GMB_LOCATIONS[locationId]
  if (known) {
    return known.city !== 'Unknown' ? `${known.name} (${known.city})` : known.name
  }
  return 'FixMyBrick Location'
}

export interface GMBReview {
  // Core review fields from reviews table
  id: string
  review_id: string
  location_id: string
  reviewer_name: string
  reviewer_comment: string
  star_rating: string // Stored as "FIVE", "FOUR", etc.
  reviewer_create_time: string
  reviewer_update_time: string
  reply_comment?: string | null
  reply_update_time?: string
  reviewer_photo_url?: string
  created_at: string
  updated_at: string
  
  // Additional fields from reviews table
  location_name?: string
  google_name?: string
  gmb_review_id?: string | null
  gmb_location_id?: string | null
  gmb_location_ref?: string | null
  source?: string
  auto_tagged?: boolean
  tag_confidence?: number
  service_id?: string | null
  create_time?: string
  update_time?: string
  
  // Computed/mapped fields
  location_display_name?: string
  address_city?: string
  franchise_region?: string
}

export interface GMBReviewsData {
  reviews: GMBReview[]
  total_count: number
  page: number
  page_size: number
  total_pages: number
}

export interface GMBReviewStats {
  total_reviews: number
  average_rating: number | null
  response_rate: number
  pending_responses: number
  reviews_this_month: number
  rating_change: number
  response_rate_change: number
}

export interface ReviewFilters {
  search?: string
  starRating?: number | 'all' // 1-5 or 'all'
  location?: string // franchise region
  responseStatus?: 'all' | 'responded' | 'needs_response'
  dateFrom?: string // ISO date string
  dateTo?: string // ISO date string
  sortBy?: 'newest' | 'oldest' | 'highest_rating' | 'lowest_rating'
}

/**
 * Hook for fetching GMB reviews directly from gmb_reviews table
 */
export function useGMBReviews(page = 1, pageSize = 20, filters?: ReviewFilters) {
  return useQuery({
    queryKey: ['gmb-reviews', page, pageSize, filters],
    queryFn: async (): Promise<GMBReviewsData> => {
      // IMPORTANT: Using 'gmb_reviews' table which contains Google My Business review data
      // This is different from the general 'reviews' table used elsewhere
      let reviewQuery = supabase
        .from('gmb_reviews')
        .select('*', { count: 'exact' })

      // Apply search filter if provided
      if (filters?.search && filters.search.trim()) {
        reviewQuery = reviewQuery.or(`reviewer_name.ilike.%${filters.search}%,reviewer_comment.ilike.%${filters.search}%`)
      }

      // Apply star rating filter
      if (filters?.starRating && filters.starRating !== 'all') {
        const ratingText = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE'][filters.starRating]
        if (ratingText) {
          reviewQuery = reviewQuery.eq('star_rating', ratingText)
        }
      }

      // Apply location filter using location_id (we'll map location filter value to location IDs)
      if (filters?.location && filters.location !== 'all') {
        const locationIds = Object.entries(KNOWN_GMB_LOCATIONS)
          .filter(([_, locationInfo]) => locationInfo.region === filters.location)
          .map(([locationId]) => locationId)
        
        if (locationIds.length > 0) {
          reviewQuery = reviewQuery.in('location_id', locationIds)
        }
      }

      // Apply response status filter
      if (filters?.responseStatus === 'responded') {
        reviewQuery = reviewQuery.not('reply_comment', 'is', null)
      } else if (filters?.responseStatus === 'needs_response') {
        reviewQuery = reviewQuery.is('reply_comment', null)
      }

      // Apply date range filters
      if (filters?.dateFrom) {
        reviewQuery = reviewQuery.gte('created_at', filters.dateFrom)
      }
      if (filters?.dateTo) {
        reviewQuery = reviewQuery.lte('created_at', filters.dateTo)
      }

      // Apply sorting
      switch (filters?.sortBy) {
        case 'oldest':
          reviewQuery = reviewQuery.order('created_at', { ascending: true })
          break
        case 'highest_rating':
          reviewQuery = reviewQuery.order('star_rating', { ascending: false }).order('created_at', { ascending: false })
          break
        case 'lowest_rating':
          reviewQuery = reviewQuery.order('star_rating', { ascending: true }).order('created_at', { ascending: false })
          break
        default: // 'newest'
          reviewQuery = reviewQuery.order('created_at', { ascending: false })
          break
      }

      // Apply pagination
      const offset = (page - 1) * pageSize
      reviewQuery = reviewQuery.range(offset, offset + pageSize - 1)

      const { data: reviewsData, count, error } = await reviewQuery

      if (error) {
        console.error('Error fetching GMB reviews:', error)
        throw new Error(`Failed to fetch GMB reviews: ${error.message}`)
      }


      // Add location information using our known mapping and existing location_name
      const reviewsWithLocations = reviewsData?.map(review => {
        const locationInfo = KNOWN_GMB_LOCATIONS[review.location_id]
        return {
          ...review,
          // Use existing location_name from database, or fallback to mapped name
          location_display_name: review.location_name || getLocationDisplayName(review.location_id),
          address_city: locationInfo?.city || review.location_name,
          franchise_region: locationInfo?.region || review.location_name?.toLowerCase().replace(/\s+/g, '-')
        }
      }) || []

      return {
        reviews: reviewsWithLocations,
        total_count: count || 0,
        page,
        page_size: pageSize,
        total_pages: Math.ceil((count || 0) / pageSize)
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  })
}

/**
 * Hook for fetching GMB review statistics directly from gmb_reviews table
 */
export function useGMBReviewStats() {
  return useQuery({
    queryKey: ['gmb-review-stats'],
    queryFn: async (): Promise<GMBReviewStats> => {
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      
      const sixtyDaysAgo = new Date()
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60)

      try {
        const computeAverageRating = (rows?: Array<{ star_rating?: string | number | null, rating?: number | null }>) => {
          const normalized = rows
            ?.map(review => normalizeStarRating(review.star_rating ?? review.rating ?? null))
            .filter(rating => rating > 0) || []

          if (normalized.length === 0) return null
          return normalized.reduce((sum, rating) => sum + rating, 0) / normalized.length
        }

        // IMPORTANT: Using 'gmb_reviews' table which contains Google My Business review data
        // Get all reviews for total count and average rating from gmb_reviews table
        const { data: allReviews, error: allError } = await supabase
          .from('gmb_reviews')
          .select('star_rating, reply_comment, created_at')

        if (allError) throw allError

        // Get current month reviews
        const { data: currentMonth, error: currentError } = await supabase
          .from('gmb_reviews')
          .select('star_rating, reply_comment')
          .gte('created_at', thirtyDaysAgo.toISOString())

        if (currentError) throw currentError

        // Get previous month reviews for comparison
        const { data: previousMonth, error: previousError } = await supabase
          .from('gmb_reviews')
          .select('star_rating, reply_comment')
          .gte('created_at', sixtyDaysAgo.toISOString())
          .lt('created_at', thirtyDaysAgo.toISOString())

        if (previousError) throw previousError

        // Calculate statistics
        const totalReviews = allReviews?.length || 0
        const reviewsThisMonth = currentMonth?.length || 0

        const averageRating = computeAverageRating(allReviews)
        const currentAvgRating = computeAverageRating(currentMonth) ?? 0
        const previousAvgRating = computeAverageRating(previousMonth) ?? 0

        const ratingChange = currentAvgRating && previousAvgRating
          ? Math.round((currentAvgRating - previousAvgRating) * 10) / 10
          : 0

        const totalResponded = allReviews?.filter(r => r.reply_comment).length || 0
        const responseRate = totalReviews > 0 ? (totalResponded / totalReviews) * 100 : 0

        const currentResponded = currentMonth?.filter(r => r.reply_comment).length || 0
        const currentResponseRate = currentMonth?.length 
          ? (currentResponded / currentMonth.length) * 100 
          : 0

        const previousResponded = previousMonth?.filter(r => r.reply_comment).length || 0
        const previousResponseRate = previousMonth?.length 
          ? (previousResponded / previousMonth.length) * 100 
          : 0

        const responseRateChange = currentResponseRate - previousResponseRate

        const pendingResponses = allReviews?.filter(r => !r.reply_comment).length || 0

        return {
          total_reviews: totalReviews,
          average_rating: averageRating !== null ? Math.round(averageRating * 10) / 10 : null,
          response_rate: Math.round(responseRate),
          pending_responses: pendingResponses,
          reviews_this_month: reviewsThisMonth,
          rating_change: ratingChange,
          response_rate_change: Math.round(responseRateChange)
        }
      } catch (error) {
        console.error('Error fetching GMB review stats:', error)
        throw new Error('Failed to fetch GMB review statistics')
      }
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  })
}
